import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { contactsApi, clientsApi } from "@/lib/apiClient";
import { PageContainer, PageHeader } from "@/components/Layout";
import { Pager, usePager } from "@/components/Pager";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Search, Mail, Phone } from "lucide-react";
import { useCan } from "@/lib/rbac";

const ROLES = ["primary", "billing", "technical", "other"];

function ContactForm({ initial, clients, onSubmit, onCancel }) {
  const [form, setForm] = useState(
    initial || { name: "", email: "", phone: "", role: "primary", client_id: clients[0]?.id || "" }
  );
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!form.name.trim()) return toast.error("Name is required");
        if (!form.client_id) return toast.error("Client is required");
        onSubmit(form);
      }}
      className="space-y-3"
      data-testid="contact-form"
    >
      <label className="block">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Client</span>
        <select
          data-testid="contact-input-client"
          value={form.client_id}
          onChange={(e) => setForm({ ...form, client_id: e.target.value })}
          className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3] outline-none bg-white"
        >
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </label>
      {[
        { k: "name", label: "Name" },
        { k: "email", label: "Email" },
        { k: "phone", label: "Phone" },
      ].map(({ k, label }) => (
        <label key={k} className="block">
          <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">{label}</span>
          <input
            data-testid={`contact-input-${k}`}
            className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3] outline-none"
            value={form[k] || ""}
            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
          />
        </label>
      ))}
      <label className="block">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">Role</span>
        <select
          data-testid="contact-input-role"
          value={form.role || "primary"}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
          className="mt-1.5 w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3] outline-none bg-white"
        >
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </label>
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm hover:bg-zinc-50">
          Cancel
        </button>
        <button data-testid="contact-save" type="submit" className="bg-[#3730A3] text-white rounded-md px-3 py-1.5 text-sm hover:bg-indigo-900">
          Save
        </button>
      </div>
    </form>
  );
}

export default function Contacts() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const canWrite = useCan("write:contacts");

  const { data = [] } = useQuery({ queryKey: ["contacts"], queryFn: () => contactsApi.list() });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: clientsApi.list });

  const clientById = Object.fromEntries(clients.map((c) => [c.id, c.name]));

  const filtered = data.filter(
    (c) =>
      !search ||
      (c.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.email || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.phone || "").includes(search)
  );
  const pager = usePager(filtered, { defaultSize: 25, sizes: [25, 50, 100] });

  const inval = () => qc.invalidateQueries({ queryKey: ["contacts"] });
  const createM = useMutation({ mutationFn: contactsApi.create, onSuccess: () => { toast.success("Contact created"); inval(); setModal(null); } });
  const updateM = useMutation({ mutationFn: ({ id, data }) => contactsApi.update(id, data), onSuccess: () => { toast.success("Contact updated"); inval(); setModal(null); } });
  const removeM = useMutation({ mutationFn: contactsApi.remove, onSuccess: () => { toast.success("Contact deleted"); inval(); } });

  return (
    <PageContainer>
      <PageHeader
        title="Contacts"
        subtitle="People associated with each client. A client may have several contacts (primary, billing, technical)."
        actions={
          canWrite ? (
            <button
              data-testid="contact-new"
              onClick={() => setModal({ mode: "create" })}
              className="bg-[#3730A3] text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-indigo-900 inline-flex items-center gap-1.5"
              disabled={clients.length === 0}
              title={clients.length === 0 ? "Create a client first" : ""}
            >
              <Plus className="w-4 h-4" /> New contact
            </button>
          ) : null
        }
      />

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-zinc-400" />
            <input
              data-testid="contact-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email or phone…"
              className="w-full pl-8 pr-3 py-2 text-sm border border-zinc-200 rounded-md focus:ring-1 focus:ring-[#3730A3] focus:border-[#3730A3] outline-none"
            />
          </div>
          <div className="text-xs text-zinc-500 font-mono">{filtered.length} total</div>
        </div>

        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="text-xs uppercase tracking-wider text-zinc-500">
              <th className="text-left p-3 font-semibold">Name</th>
              <th className="text-left p-3 font-semibold">Client</th>
              <th className="text-left p-3 font-semibold">Email</th>
              <th className="text-left p-3 font-semibold">Phone</th>
              <th className="text-left p-3 font-semibold">Role</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {pager.pageRows.map((c) => (
              <tr key={c.id} className="border-t border-zinc-100 hover:bg-zinc-50/60" data-testid={`contact-row-${c.id}`}>
                <td className="p-3 font-medium">{c.name}</td>
                <td className="p-3 text-zinc-600">{clientById[c.client_id] || "—"}</td>
                <td className="p-3 text-zinc-600 text-sm">
                  <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 hover:text-zinc-900">
                    <Mail className="w-3.5 h-3.5 text-zinc-400" /> {c.email || "—"}
                  </a>
                </td>
                <td className="p-3 text-zinc-600 text-sm font-mono">
                  {c.phone ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5 text-zinc-400" /> {c.phone}
                    </span>
                  ) : "—"}
                </td>
                <td className="p-3">
                  <span className="text-[10px] uppercase tracking-widest font-mono bg-zinc-100 text-zinc-700 px-1.5 py-0.5 rounded">
                    {c.role || "—"}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    {canWrite && (
                      <>
                        <button
                          data-testid={`contact-edit-${c.id}`}
                          onClick={() => setModal({ mode: "edit", row: c })}
                          className="text-zinc-600 hover:text-zinc-900 p-1.5 rounded hover:bg-zinc-100"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`contact-delete-${c.id}`}
                          onClick={() => {
                            if (window.confirm(`Delete "${c.name}"?`)) removeM.mutate(c.id);
                          }}
                          className="text-red-500 hover:text-red-700 p-1.5 rounded hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {pager.total === 0 && (
              <tr>
                <td colSpan={6} className="py-10 text-center text-sm text-zinc-500">
                  No contacts found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <Pager {...pager} testidPrefix="contact-pager" />
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-zinc-200 rounded-lg w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-heading text-lg font-bold mb-3">
              {modal.mode === "create" ? "New contact" : "Edit contact"}
            </h2>
            <ContactForm
              initial={modal.row}
              clients={clients}
              onCancel={() => setModal(null)}
              onSubmit={(form) => {
                if (modal.mode === "create") createM.mutate(form);
                else updateM.mutate({ id: modal.row.id, data: form });
              }}
            />
          </div>
        </div>
      )}
    </PageContainer>
  );
}
